import { pipe } from 'ramda';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Maybe } from 'core/maybe';
import { map, ofNullable, ifEmpty, orElse } from 'core/maybe';
import { useLatestRef } from './use-latest';

export interface Serializer<T> {
  deserialize: (from: Maybe<string>) => Maybe<T>;
  serialize: (value: T) => string;
}

const defaultSerializer = {
  deserialize: map(JSON.parse),
  serialize: (value) => JSON.stringify(value),
};

export const useLocalStorage = <T>(
  key: string,
  otherwise: T,
  { deserialize, serialize }: Serializer<T> = defaultSerializer,
): [T, Dispatch<SetStateAction<T>>] => {
  const oldKeyRef = useRef(key);
  const refFree = useLatestRef({ deserialize, otherwise, serialize });

  const getter = useMemo(
    () =>
      pipe(
        () => window.localStorage.getItem(key),
        ofNullable,
        refFree.current.deserialize,
        ifEmpty(() =>
          window.localStorage.setItem(
            key,
            refFree.current.serialize(refFree.current.otherwise),
          ),
        ),
        orElse(refFree.current.otherwise),
      ),
    [key, refFree],
  );

  const [value, setValue] = useState<T>(getter);

  useEffect(() => {
    if (oldKeyRef.current !== key) {
      setValue(getter());
      oldKeyRef.current = key;
    }
  }, [getter, key]);

  const listener = useCallback(
    (e: StorageEvent) => {
      if (e.storageArea !== localStorage || e.key !== key) {
        return;
      }
      const next = pipe(
        () => e.newValue,
        ofNullable,
        deserialize,
        orElse(otherwise),
      )();

      if (serialize(value) !== serialize(next)) {
        setValue(next);
      }
    },
    [key, otherwise, value, deserialize, serialize, setValue],
  );

  useEffect(() => {
    window.addEventListener('storage', listener);

    return () => window.removeEventListener('storage', listener);
  }, [listener]);

  useEffect(() => {
    window.localStorage.setItem(key, serialize(value));
    window.dispatchEvent(
      new StorageEvent('storage', {
        storageArea: window.localStorage,
        key,
        newValue: serialize(value),
      }),
    );
  }, [key, serialize, value]);

  return [value, setValue];
};
